import React, { useCallback, useState } from 'react'
import { useNotification } from '../../../../hooks/useNotification';
import { useErrorInputs } from '../../../../hooks/useErrorInputs';
import { useForm } from '../../../../hooks/useForm';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Dialog from '@mui/material/Dialog';
import Box from '@mui/material/Box';
import SettingsBrightnessIcon from '@mui/icons-material/SettingsBrightness';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import AddIcon from '@mui/icons-material/Add';
import Close from '@mui/icons-material/Close';
import { ActionButtons } from '../../../../components/others/ActionButtons';
import { ChartReportForm } from './ChartReportForm';
import { MenuAndSubmenu } from './MenuAndSubmenu';
import { ManageActions } from './ManageActions';


import { safeParseJSON } from '../../../../utils/jsonValidator';
import { sanitizeAction } from '../../helpers/helpers';

const ACTION_CONFIG = {
    listColumns:  { icon: <ViewColumnIcon />,        tooltip: 'interface.listColumns'   },
    createButton: { icon: <AddIcon />, tooltip: 'interface.createButton'  },
    default:      { icon: <SettingsBrightnessIcon />, tooltip: 'interface.addConfiguration' },
};

export const ManageConfiguration = ({ data, typeAction, isDisabled, isEdit, addedConfiguration, t, action=false, inline = false }) => {
    const [values, handleInputChange, handleDirectInputChange, , reset] = useForm(data);
    const [errorInputs, , resetErrors, validForm] = useErrorInputs({});
    const [selectedItem, setSelectedItem] = useState(null);
    const [openModal, setOpenModal] = useState(false);
    const [sendNotification] = useNotification();

    const handleOpenModal = (item = null) => {
        reset();
        setSelectedItem(item);
        setOpenModal(true);
    };

    const handleCloseModal = () => {
        setOpenModal(false);
        resetErrors();
        reset();
        setSelectedItem(null);
    };

    const setData = useCallback((key, val) => {
        handleDirectInputChange(key, val);
    }, []);

    const saveIfValid = (condition, errorMsg, configValue = values) => {
        if (condition) {
            addedConfiguration('configuration', configValue);
            handleCloseModal();
        } else
            sendNotification({ msg: errorMsg, variant: 'error' });
    };

    const handleSave = () => {
        if (['MENU', 'SUBMENU'].includes(typeAction) && action === 'listColumns') {
            const currentConfig = safeParseJSON(data);
            saveIfValid(
                'table' in values && values.table.length > 1,
                'Debe agregar por lo menos dos columnas a la tabla.',
                { ...currentConfig, table: values.table }
            );
        } else if (typeAction === 'MENU' && action === 'createButton') {
            const currentConfig = safeParseJSON(data);
            if (Array.isArray(values.button) && values.button.length > 0) {
                saveIfValid(true, '', {
                    ...currentConfig,
                    button: values.button,
                });
            } else {
                sendNotification({ msg: 'Debe agregar por lo menos un botón.', variant: 'error' });
            }
        } else if (['BUTTON', 'API', 'FLOW_PROCESS'].includes(typeAction)) {
            if (validForm(['accion', 'icon', 'color', 'type'], values)) {
                saveIfValid(true, '', sanitizeAction(values));
            } else {
                sendNotification({ msg: 'Debe completar el formulario.', variant: 'error' });
            }
        } else if (typeAction === 'CHART_REPORT') {
            saveIfValid(
                values?.charts?.length > 0,
                'Debe agregar por lo menos una gráfica.'
            );
        }
    };

    const { icon, tooltip } = ACTION_CONFIG[action] || ACTION_CONFIG.default;

    const triggerButton = (
        <Tooltip title={t(tooltip)}>
            <span>
                <IconButton disabled={isDisabled} onClick={() => handleOpenModal()} size="small" sx={{ height: 20 }}>
                    {icon}
                </IconButton>
            </span>
        </Tooltip>
    );

    return (
        <Box sx={{ width: inline ? 'auto' : '100%' }}>
            {!inline && (
                <Box sx={{ pt: action === 'createButton' ? 0 : 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: action === 'createButton' ? 4 : 2 }}>
                        {triggerButton}
                    </Box>
                </Box>
            )}

            {inline && triggerButton}

            <Dialog open={openModal} fullWidth maxWidth='md' >
                <DialogTitle>
                    <Typography variant="h5" component="span">{action === 'createButton' ? t('interface.createButton') : t('interface.createEditConfiguration')}</Typography>
                    <IconButton
                        onClick={handleCloseModal}
                        sx={{
                            position: 'absolute',
                            right: 8,
                            top: 8,
                            color: (theme) => theme.palette.grey[500],
                        }}
                    >
                        <Close />
                    </IconButton>
                </DialogTitle>

                <DialogContent>
                    {(['MENU', 'SUBMENU'].includes(typeAction) && action === 'listColumns') &&
                        <MenuAndSubmenu
                            data={data}
                            typeAction={typeAction}
                            handleSave={setData}
                            handleCloseModal={handleCloseModal}
                            t={t}
                            action={action}
                        />
                    }
                    {typeAction === 'MENU' && action === 'createButton' &&
                        <MenuAndSubmenu
                            data={data}
                            typeAction={typeAction}
                            handleSave={setData}
                            handleCloseModal={handleCloseModal}
                            t={t}
                            action={action}
                        />
                    }
                    {(['BUTTON', 'API', 'FLOW_PROCESS'].includes(typeAction)) &&
                        <ManageActions
                            values={values}
                            errorInputs={errorInputs}
                            listActions={false}
                            data={selectedItem || {}}
                            handleInputChange={handleInputChange}
                            handleDirectInputChange={handleDirectInputChange}
                            handleCloseModal={handleCloseModal}
                            t={t}
                        />
                    }
                    {['CHART_REPORT'].includes(typeAction) &&
                        <ChartReportForm
                            data={data}
                            handleDirectInputChange={handleDirectInputChange}
                            t={t}
                        />
                    }
                </DialogContent>
                <DialogActions>
                    <ActionButtons onSubmit={handleSave} onCancel={handleCloseModal} />
                </DialogActions>
            </Dialog>
        </Box>
    );
}    